import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import ComputerIcon from "@mui/icons-material/Computer";
import LaunchIcon from "@mui/icons-material/Launch";
import SwapHorizontalCircleIcon from "@mui/icons-material/SwapHorizontalCircle";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import CopyrightIcon from "@mui/icons-material/Copyright";
import LocalAtmIcon from "@mui/icons-material/LocalAtm";
import SecurityIcon from "@mui/icons-material/Security";
import CodeIcon from "@mui/icons-material/Code";
import CloudIcon from "@mui/icons-material/Cloud";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk";
import { getImageUrl } from "../../../utils/helpers";

const scrollToSection = (id) => {
  const element = document.getElementById(id);
  if (element) {
    element.scrollIntoView({ behavior: "smooth" });
  }
};

const AiTechnologyTransactions = ({ baseConfig }) => {
  const { priColor, priHoverColor, tertColor, tertHoverColor } =
    baseConfig.siteBranding;

  const transactionTypes = [
    {
      title: "AI Software Licensing",
      icon: <CloudIcon />,
      description:
        "Comprehensive agreements for licensing proprietary AI software, models, and algorithms to other businesses.",
    },
    {
      title: "SaaS & Subscription Agreements",
      icon: <ComputerIcon />,
      description:
        "Contracts for cloud-based AI applications offered on a subscription basis, with appropriate service level agreements.",
    },
    {
      title: "Data & Model Licensing",
      icon: <SwapHorizontalCircleIcon />,
      description:
        "Specialized agreements governing the sharing and use of training data, pre-trained models, and AI-generated outputs.",
    },
    {
      title: "Development & Co-Development",
      icon: <CodeIcon />,
      description:
        "Contracts for custom AI development, with clear terms on IP ownership, milestones, and acceptance criteria.",
    },
    {
      title: "Strategic Partnerships",
      icon: <LocalAtmIcon />,
      description:
        "Complex agreements governing joint ventures, revenue sharing, and strategic collaborations involving AI technologies.",
    },
    {
      title: "API Integration Agreements",
      icon: <CopyrightIcon />,
      description:
        "Terms governing the integration and use of third-party AI APIs within your applications or services.",
    },
  ];

  const legalIssues = [
    {
      title: "Intellectual Property Rights",
      description:
        "Clear allocation of ownership rights to AI models, training data, and outputs",
    },
    {
      title: "Liability & Indemnification",
      description:
        "Appropriate allocation of risk for AI system failures or inaccuracies",
    },
    {
      title: "Data Security & Privacy",
      description:
        "Robust provisions governing data handling, protection, and compliance with regulations",
    },
    {
      title: "Performance Metrics",
      description:
        "Clearly defined performance standards and remedies for underperformance",
    },
    {
      title: "Regulatory Compliance",
      description:
        "Addressing evolving AI-specific regulations and industry standards",
    },
    {
      title: "Exit Strategies",
      description:
        "Well-defined provisions for termination, transition, and data disposal",
    },
  ];

  return (
    <Box sx={{ mb: 6 }}>
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          backgroundColor: `${tertColor}22`,
          borderRadius: "4px 4px 0 0",
        }}
      >
        <SwapHorizontalCircleIcon sx={{ mr: 2, color: tertColor }} />
        <Typography variant="h5" fontWeight="bold">
          AI Technology Transactions
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 4 },
          border: `1px solid ${tertColor}33`,
          borderTop: 0,
          borderRadius: "0 0 4px 4px",
        }}
      >
        {/* Top Section */}
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Typography
              variant="h6"
              fontWeight="bold"
              color={tertColor}
              gutterBottom
            >
              Navigating Complex AI Technology Agreements
            </Typography>

            <Typography variant="body1" paragraph>
              At Kama Thuo, PLLC, we specialize in negotiating, drafting, and
              reviewing complex technology agreements involving AI solutions.
              Our services cover AI software licensing agreements, SaaS
              contracts, model and data licensing agreements, technology
              transfer contracts, development and co-development agreements, and
              strategic partnership agreements.
            </Typography>

            <Typography variant="body1" paragraph>
              We provide counsel to clients on intricate issues involving
              intellectual property rights allocation, liability, data
              ownership, security compliance, indemnification clauses, and exit
              strategies for AI-related technology collaborations and
              transactions.
            </Typography>

            <Typography variant="body1" paragraph>
              Our team brings deep technical knowledge of AI systems combined
              with legal expertise, allowing us to craft agreements that address
              the unique challenges presented by artificial intelligence
              technologies, including evolving regulatory requirements and
              industry-specific considerations.
            </Typography>

            <Box sx={{ mt: 3 }}>
              <Button
                startIcon={<LaunchIcon />}
                variant="contained"
                sx={{
                  backgroundColor: priColor,
                  color: "#fff",
                  "&:hover": { backgroundColor: priHoverColor, color: "#fff" },
                }}
                component={RouterLink}
                to="/news-insights?category=tech-transactions"
              >
                Learn More: Technology Transactions
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box
              component="img"
              src={getImageUrl("kth-ai-transactions.png")}
              alt="AI Technology Transactions"
              sx={{
                width: "100%",
                height: "auto",
                borderRadius: 2,
                boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
              }}
            />
          </Grid>
        </Grid>

        {/* Full Width Card */}
        <Box sx={{ mt: 5, mb: 6 }}>
          <Card
            elevation={2}
            sx={{
              borderRadius: 2,
              border: `1px solid ${tertColor}33`,
            }}
          >
            <CardContent>
              <Typography
                variant="h6"
                fontWeight="bold"
                color={tertColor}
                gutterBottom
              >
                Why AI Transactions Require Specialized Legal Expertise
              </Typography>

              <Typography variant="body2" paragraph>
                AI technology transactions present unique legal challenges due
                to:
              </Typography>

              <List dense>
                {[
                  "Complex intellectual property considerations around machine learning models and data",
                  "Evolving regulatory landscapes requiring forward-thinking compliance provisions",
                  "Unique liability and risk allocation issues related to AI system performance",
                  "Data rights and privacy considerations that extend beyond traditional software agreements",
                  "Technical complexity requiring legal counsel with deep understanding of AI technologies",
                ].map((text, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <ArrowRightIcon sx={{ color: tertColor }} />
                    </ListItemIcon>
                    <ListItemText primary={text} />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Box>

        {/* Transaction Types */}
        <Box sx={{ mb: 6 }}>
          <Typography
            variant="h6"
            fontWeight="bold"
            color={tertColor}
            gutterBottom
          >
            AI Transaction Types We Specialize In
          </Typography>

          <Grid container spacing={3}>
            {transactionTypes.map((type, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  elevation={1}
                  sx={{
                    height: "100%",
                    transition: "transform 0.3s ease, box-shadow 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-5px)",
                      boxShadow: "0 8px 25px rgba(0,0,0,0.1)",
                    },
                  }}
                >
                  <CardContent>
                    <Box display="flex" alignItems="center" mb={1}>
                      <Box sx={{ color: tertColor, mr: 1.5 }}>{type.icon}</Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {type.title}
                      </Typography>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    <Typography variant="body2">{type.description}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Legal Issues Section */}
        <Divider sx={{ my: 4 }} />

        <Box>
          <Typography
            variant="h6"
            fontWeight="bold"
            color={tertColor}
            gutterBottom
          >
            Critical Legal Issues in AI Technology Transactions
          </Typography>

          <Grid container spacing={4}>
            <Grid item xs={12} md={7}>
              <Typography variant="body1" paragraph sx={{ mb: 4 }}>
                AI-related technology transactions require careful consideration
                of numerous complex legal issues beyond those found in
                traditional software or technology agreements. Our firm helps
                clients navigate these challenges by developing comprehensive
                agreements that address the unique aspects of artificial
                intelligence systems, including:
              </Typography>

              <Grid container spacing={2}>
                {legalIssues.map((issue, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Box sx={{ display: "flex", mb: 2 }}>
                      <VerifiedUserIcon
                        sx={{ color: tertColor, mr: 1.5, mt: 0.5 }}
                      />
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {issue.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {issue.description}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>

            <Grid item xs={12} md={5}>
              <Paper
                elevation={3}
                sx={{
                  p: 2,
                  backgroundColor: "#f9f9f9",
                  borderRadius: 2,
                  border: `1px solid ${tertColor}33`,
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  color={tertColor}
                  // gutterBottom
                >
                  Our Technology Transaction Services
                </Typography>

                <List>
                  <ListItem>
                    <ListItemIcon>
                      <SecurityIcon sx={{ color: tertColor }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Agreement Drafting & Review"
                      secondary="Creating custom agreements or reviewing proposed contracts from counterparties"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <SwapHorizontalCircleIcon sx={{ color: tertColor }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Negotiation Support"
                      secondary="Technical and legal advice during contract negotiations with vendors, customers, or partners"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <LocalAtmIcon sx={{ color: tertColor }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Risk Assessment"
                      secondary="Evaluating contracts for legal, commercial, and technical risks specific to AI technologies"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CopyrightIcon sx={{ color: tertColor }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="IP Strategy"
                      secondary="Developing comprehensive intellectual property strategies for AI innovations"
                    />
                  </ListItem>
                </List>

                <Box sx={{ mt: 2, ml: 8 }}>
                  <Button
                    startIcon={<PhoneInTalkIcon />}
                    variant="contained"
                    onClick={() => scrollToSection("contact-section")}
                    sx={{
                      backgroundColor: tertColor,
                      color: "white",
                      "&:hover": {
                        color: "white",
                        backgroundColor: tertHoverColor,
                      },
                    }}
                  >
                    Schedule a Consultation
                  </Button>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
};

export default AiTechnologyTransactions;
